import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Pencil, Plus, Upload, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const inputCls =
  "w-full bg-jm-ink/60 border border-jm-brass/25 focus:border-jm-amber px-3.5 py-2.5 text-sm text-jm-bone placeholder:text-jm-mute focus:outline-none transition-colors";

const PRODUCT_CATEGORIES = ["CD", "Vinyle", "Téléchargement", "Édition limitée", "Affiche", "Textile", "Objet"];
const RELEASE_TYPES = ["Album", "EP", "Single", "Vidéo", "Session live", "Édition spéciale"];
const TRACK_MOODS = ["Chilling", "Cooling", "Grooving", "Film making", "Walking", "Intensifying", "Fast energising", "Chaos Engineering"];

const CRUD_TABS = [
  {
    id: "news",
    label: "Actualités",
    display: (i) => i.title,
    sub: (i) => `${i.date} · ${i.artist || ""}`,
    fields: [
      { name: "title", label: "Titre", type: "text", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "artist", label: "Projet", type: "artist" },
      { name: "excerpt", label: "Résumé", type: "textarea" },
      { name: "content", label: "Texte complet", type: "textarea" },
    ],
  },
  {
    id: "concerts",
    label: "Concerts",
    display: (i) => `${i.venue} — ${i.city}`,
    sub: (i) => `${i.date} · ${i.artist || ""}`,
    fields: [
      { name: "artist", label: "Projet", type: "artist", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "city", label: "Ville", type: "text", required: true },
      { name: "venue", label: "Salle / Festival", type: "text", required: true },
      { name: "ticket_url", label: "Lien billetterie (optionnel)", type: "text" },
    ],
  },
  {
    id: "releases",
    label: "Sorties / Éditions",
    display: (i) => i.title,
    sub: (i) => `${i.type || ""} · ${i.year || ""}`,
    fields: [
      { name: "title", label: "Titre", type: "text", required: true },
      { name: "type", label: "Type", type: "select", options: RELEASE_TYPES },
      { name: "year", label: "Année", type: "text" },
      { name: "artist", label: "Projet", type: "artist" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "listen_url", label: "Lien d'écoute (optionnel)", type: "text" },
    ],
  },
  {
    id: "products",
    label: "Boutique",
    display: (i) => i.name,
    sub: (i) => `${i.category || ""} · ${i.price} €`,
    fields: [
      { name: "name", label: "Nom", type: "text", required: true },
      { name: "price", label: "Prix (€)", type: "number", required: true },
      { name: "category", label: "Catégorie", type: "select", options: PRODUCT_CATEGORIES },
      { name: "artist", label: "Projet associé", type: "artist" },
      { name: "badge", label: "Badge (ex : Précommande)", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "preorder", label: "Précommande", type: "checkbox" },
    ],
  },
  {
    id: "tracks",
    label: "Jukebox",
    display: (i) => i.title,
    sub: (i) => `Station : ${i.artist || "—"}${i.mood ? ` · ${i.mood}` : ""}${i.note ? ` · ${i.note}` : ""}`,
    fields: [
      { name: "title", label: "Titre du morceau", type: "text", required: true },
      { name: "artist", label: "Station (projet)", type: "artist", required: true },
      { name: "mood", label: "Ambiance (catégorie du tuner)", type: "select", options: TRACK_MOODS },
      { name: "url", label: "Fichier audio (mp3, wav)", type: "audio", required: true },
      { name: "note", label: "Note (optionnel : session, date…)", type: "text" },
    ],
  },
];

function AudioField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd);
      onChange(res.data.url);
      toast.success("Fichier audio en ligne");
    } catch {
      toast.error("Envoi impossible");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div data-testid="audio-field">
      <label className="flex items-center gap-3 border border-dashed border-jm-brass/30 hover:border-jm-amber px-4 py-3 cursor-pointer transition-colors">
        <Upload size={15} className="text-jm-amber shrink-0" />
        <span className="text-sm text-jm-smoke truncate">
          {uploading ? "Envoi en cours…" : value ? "Fichier chargé — cliquer pour remplacer" : "Choisir un fichier audio"}
        </span>
        <input type="file" accept="audio/mpeg,audio/wav,.mp3,.wav" className="hidden" onChange={upload} disabled={uploading} />
      </label>
      {value && <p className="mt-2 font-mono text-[10px] text-jm-mute truncate" data-testid="audio-field-url">{value}</p>}
    </div>
  );
}

function Field({ field, value, onChange, artists }) {
  if (field.type === "audio") return <AudioField value={value} onChange={onChange} />;
  if (field.type === "textarea")
    return <textarea rows={3} className={inputCls} value={value || ""} required={field.required} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "checkbox")
    return (
      <label className="flex items-center gap-3 text-sm text-jm-smoke cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-jm-amber w-4 h-4" />
        {field.label}
      </label>
    );
  if (field.type === "select" || field.type === "artist") {
    const options =
      field.type === "artist"
        ? [{ v: "joy-messenger", l: "Joy Messenger (maison)" }, ...artists.map((a) => ({ v: a.id, l: a.name }))]
        : field.options.map((o) => ({ v: o, l: o }));
    return (
      <select className={inputCls} value={value || ""} required={field.required} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={field.type}
      step={field.type === "number" ? "0.01" : undefined}
      className={inputCls}
      value={value ?? ""}
      required={field.required}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
    />
  );
}

function CrudTab({ tab, artists }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/${tab.id}`).then((r) => setItems(r.data)).catch(() => {});
  }, [tab.id]);

  useEffect(() => {
    load();
    setEditing(null);
    setForm({});
  }, [load]);

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ ...item });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/${tab.id}/${editing}`, form);
        toast.success("Élément mis à jour");
      } else {
        await api.post(`/admin/${tab.id}`, form);
        toast.success("Élément créé");
      }
      setEditing(null);
      setForm({});
      load();
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await api.delete(`/admin/${tab.id}/${id}`);
      toast.success("Élément supprimé");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div data-testid={`admin-tab-${tab.id}`}>
      <form onSubmit={save} className="border border-jm-brass/20 bg-jm-card/60 p-6 space-y-4 mb-10" data-testid={`admin-form-${tab.id}`}>
        <p className="font-serif text-xl text-jm-bone flex items-center gap-2">
          <Plus size={16} className="text-jm-amber" />
          {editing ? "Modifier l'élément" : `Nouveau — ${tab.label}`}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {tab.fields.map((f) => (
            <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              {f.type !== "checkbox" && (
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mb-2">{f.label}</label>
              )}
              <Field field={f} value={form[f.name]} artists={artists} onChange={(v) => setForm({ ...form, [f.name]: v })} />
            </div>
          ))}
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid={`admin-save-${tab.id}`}
            className="bg-jm-amber text-jm-ink px-6 py-2.5 text-sm font-medium hover:bg-jm-gold transition-colors disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={() => { setEditing(null); setForm({}); }}
              className="border border-jm-brass/30 px-6 py-2.5 text-sm text-jm-smoke hover:text-jm-bone transition-colors"
            >
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="divide-y divide-jm-brass/10 border-y border-jm-brass/10" data-testid={`admin-list-${tab.id}`}>
        {items.length === 0 && <p className="py-8 text-sm text-jm-mute">Aucun élément.</p>}
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 py-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-jm-bone truncate">{tab.display(item)}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-jm-mute mt-1">{tab.sub(item)}</p>
            </div>
            <button
              data-testid={`admin-edit-${item.id}`}
              onClick={() => startEdit(item)}
              className="p-2 text-jm-smoke hover:text-jm-amber transition-colors"
              aria-label="Modifier"
            >
              <Pencil size={15} />
            </button>
            <button
              data-testid={`admin-delete-${item.id}`}
              onClick={() => remove(item.id)}
              className="p-2 text-jm-smoke hover:text-red-400 transition-colors"
              aria-label="Supprimer"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtistsTab({ artists, reloadArtists }) {
  const [slug, setSlug] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const select = (id) => {
    setSlug(id);
    const a = artists.find((x) => x.id === id);
    setForm({
      tagline: a.tagline || "",
      genre: a.genre || "",
      phrase: a.phrase || "",
      hero_video: a.hero_video || "",
      hero_image: a.hero_image || "",
      bio: (a.bio || []).join("\n\n"),
    });
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, hero_video: data.url }));
      toast.success("Vidéo téléversée — pensez à enregistrer");
    } catch {
      toast.error("Téléversement impossible (formats : mp4, webm, mov, images, audio, pdf)");
    } finally {
      setUploading(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, hero_image: data.url }));
      toast.success("Photo téléversée — pensez à enregistrer");
    } catch {
      toast.error("Téléversement impossible (formats : jpg, png, webp)");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/artists/${slug}`, { ...form, bio: form.bio.split(/\n\s*\n/).filter(Boolean) });
      toast.success("Projet mis à jour");
      reloadArtists();
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-tab-artists">
      <div className="flex flex-wrap gap-2.5 mb-8">
        {artists.map((a) => (
          <button
            key={a.id}
            data-testid={`admin-artist-select-${a.id}`}
            onClick={() => select(a.id)}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border transition-colors ${
              slug === a.id ? "border-jm-amber bg-jm-amber text-jm-ink" : "border-jm-brass/25 text-jm-smoke hover:text-jm-bone"
            }`}
          >
            {a.name}
          </button>
        ))}
      </div>
      {form && (
        <form onSubmit={save} className="border border-jm-brass/20 bg-jm-card/60 p-6 space-y-4" data-testid="admin-artist-form">
          <p className="font-serif text-xl text-jm-bone">
            {artists.find((a) => a.id === slug)?.name}
          </p>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mb-2">Phrase artistique courte (tagline)</label>
            <input className={inputCls} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mb-2">Genres</label>
            <input className={inputCls} value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mb-2">Citation / phrase artistique</label>
            <input className={inputCls} value={form.phrase} onChange={(e) => setForm({ ...form, phrase: e.target.value })} />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mb-2">Biographie (paragraphes séparés par une ligne vide)</label>
            <textarea rows={5} className={inputCls} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mb-2">
                Vidéo d'ouverture (grande vidéo page d'accueil)
              </label>
              <input
                className={inputCls}
                placeholder="/api/media/… ou URL externe"
                value={form.hero_video}
                onChange={(e) => setForm({ ...form, hero_video: e.target.value })}
                data-testid="admin-artist-video-url"
              />
              <label
                className="mt-3 inline-flex items-center gap-2 border border-jm-brass/30 px-4 py-2.5 text-[13px] text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors cursor-pointer"
                data-testid="admin-artist-video-upload"
              >
                <Upload size={14} />
                {uploading ? "Téléversement…" : "Téléverser une vidéo (mp4/webm)"}
                <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadVideo} disabled={uploading} />
              </label>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mb-2">Image d'ouverture (photo)</label>
              <input
                className={inputCls}
                placeholder="Vide = placeholder élégant"
                value={form.hero_image}
                onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
                data-testid="admin-artist-image-url"
              />
              <label
                className="mt-3 inline-flex items-center gap-2 border border-jm-brass/30 px-4 py-2.5 text-[13px] text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors cursor-pointer"
                data-testid="admin-artist-image-upload"
              >
                <Upload size={14} />
                {uploading ? "Téléversement…" : "Téléverser une photo (jpg/png/webp)"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} disabled={uploading} />
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-artist-save"
            className="bg-jm-amber text-jm-ink px-6 py-2.5 text-sm font-medium hover:bg-jm-gold transition-colors disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : "Enregistrer le projet"}
          </button>
        </form>
      )}
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-tab-messages" className="space-y-4">
      {messages.length === 0 && <p className="py-8 text-sm text-jm-mute">Aucun message pour le moment.</p>}
      {messages.map((m) => (
        <div key={m.id} className="border border-jm-brass/15 bg-jm-card/60 p-5" data-testid={`admin-message-${m.id}`}>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] bg-jm-amber/15 text-jm-amber px-2 py-1">{m.kind}</span>
            <span className="text-sm text-jm-bone">{m.name}</span>
            <a href={`mailto:${m.email}`} className="text-sm text-jm-brass hover:text-jm-amber">{m.email}</a>
            <span className="font-mono text-[10px] text-jm-mute ml-auto">{new Date(m.created_at).toLocaleDateString("fr-FR")}</span>
          </div>
          {m.subject && <p className="text-sm text-jm-smoke font-medium mb-1">{m.subject}</p>}
          <p className="text-sm text-jm-smoke leading-relaxed whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function NewsletterTab() {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-tab-newsletter">
      <p className="font-serif text-2xl text-jm-bone mb-6">{subs.length} inscrit{subs.length > 1 ? "s" : ""} à la newsletter</p>
      <div className="divide-y divide-jm-brass/10 border-y border-jm-brass/10">
        {subs.map((s) => (
          <div key={s.email} className="flex items-center justify-between py-3.5">
            <span className="text-sm text-jm-bone">{s.email}</span>
            <span className="font-mono text-[10px] text-jm-mute">
              {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString("fr-FR") : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TABS = [...CRUD_TABS.map((t) => ({ id: t.id, label: t.label })), { id: "artists", label: "Projets artistes" }, { id: "messages", label: "Messages" }, { id: "newsletter", label: "Newsletter" }];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("news");
  const [artists, setArtists] = useState([]);

  const loadArtists = useCallback(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) loadArtists();
  }, [user, loadArtists]);

  if (user === null) return <main className="min-h-screen bg-jm-ink" data-testid="admin-loading" />;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const crudTab = CRUD_TABS.find((t) => t.id === tab);

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-jm-ink pt-8 pb-24">
      <div className="jm-container">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b border-jm-brass/15">
          <div>
            <p className="jm-overline mb-2">Joy Messenger</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-jm-bone">Administration</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="inline-flex items-center gap-2 border border-jm-brass/30 px-4 py-2.5 text-[13px] text-jm-smoke hover:text-jm-bone transition-colors"
            >
              <ExternalLink size={14} /> Voir le site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="inline-flex items-center gap-2 border border-jm-brass/30 px-4 py-2.5 text-[13px] text-jm-smoke hover:text-red-400 hover:border-red-400/40 transition-colors"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 py-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-button-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                tab === t.id ? "border-jm-amber bg-jm-amber text-jm-ink" : "border-jm-brass/25 text-jm-smoke hover:text-jm-bone"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {crudTab && <CrudTab key={crudTab.id} tab={crudTab} artists={artists} />}
        {tab === "artists" && <ArtistsTab artists={artists} reloadArtists={loadArtists} />}
        {tab === "messages" && <MessagesTab />}
        {tab === "newsletter" && <NewsletterTab />}
      </div>
    </main>
  );
}
